import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Music,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Volume2,
  VolumeX,
  Search,
  Dumbbell,
  Zap,
  Heart,
  Clock,
  Loader2,
  LogIn,
  Crown,
  X,
  ChevronRight,
  Flame,
  Sparkles,
  Timer,
  ListMusic,
  Bike,
  Wind,
  Activity
} from 'lucide-react';

const WORKOUT_GENRES = [
  { id: 'edm', name: 'EDM', icon: '⚡' },
  { id: 'hip-hop', name: 'Hip Hop', icon: '🎤' },
  { id: 'rock', name: 'Rock', icon: '🎸' },
  { id: 'pop', name: 'Pop', icon: '🎵' },
  { id: 'electronic', name: 'Electronic', icon: '🎹' },
  { id: 'latin', name: 'Latin', icon: '💃' },
];

const ENERGY_LEVELS = [
  { id: 'high', name: 'High Energy', description: 'Intense cardio & HIIT', color: 'bg-red-500' },
  { id: 'medium', name: 'Medium', description: 'Strength training', color: 'bg-yellow-500' },
  { id: 'low', name: 'Cool Down', description: 'Stretching & yoga', color: 'bg-green-500' },
];

// Workout DJ workout types with icons
const WORKOUT_DJ_TYPES = [
  { id: 'hiit', name: 'HIIT', icon: Zap, color: 'text-red-500', bgColor: 'bg-red-500/20' },
  { id: 'strength', name: 'Strength', icon: Dumbbell, color: 'text-orange-500', bgColor: 'bg-orange-500/20' },
  { id: 'cardio', name: 'Cardio', icon: Activity, color: 'text-pink-500', bgColor: 'bg-pink-500/20' },
  { id: 'cycling', name: 'Cycling', icon: Bike, color: 'text-blue-500', bgColor: 'bg-blue-500/20' },
  { id: 'yoga', name: 'Yoga', icon: Wind, color: 'text-green-500', bgColor: 'bg-green-500/20' },
  { id: 'crossfit', name: 'CrossFit', icon: Flame, color: 'text-yellow-500', bgColor: 'bg-yellow-500/20' },
];

const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90];

export default function WorkoutMusicPage() {
  const { token } = useAuth();
  
  // Spotify auth state
  const [spotifyToken, setSpotifyToken] = useState(localStorage.getItem('spotify_access_token'));
  const [spotifyUser, setSpotifyUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('dj'); // 'dj', 'search', 'playlists'
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState('edm');
  const [selectedEnergy, setSelectedEnergy] = useState('high');
  
  // Workout DJ state
  const [djWorkoutType, setDjWorkoutType] = useState('hiit');
  const [djDuration, setDjDuration] = useState(30);
  const [djIncludeWarmup, setDjIncludeWarmup] = useState(true);
  const [djIncludeCooldown, setDjIncludeCooldown] = useState(true);
  const [djPlaylist, setDjPlaylist] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Playback state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(50);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [currentPlaylistTracks, setCurrentPlaylistTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  
  // Playlists
  const [featuredPlaylists, setFeaturedPlaylists] = useState([]);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  
  // Player SDK
  const playerRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [playerDeviceId, setPlayerDeviceId] = useState(null);
  const previewAudioRef = useRef(null);
  
  // Check for OAuth callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      handleOAuthCallback(code);
    }
  }, []);
  
  // Load Spotify user profile when token is available
  useEffect(() => {
    if (spotifyToken) {
      loadSpotifyProfile();
      loadFeaturedPlaylists();
      loadDevices();
    }
  }, [spotifyToken]);
  
  // Initialize Spotify Web Playback SDK
  useEffect(() => {
    if (!spotifyToken || !isPremium) return;
    
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    document.body.appendChild(script);
    
    window.onSpotifyWebPlaybackSDKReady = () => {
      const player = new window.Spotify.Player({
        name: 'BBA Workout Player',
        getOAuthToken: cb => { cb(spotifyToken); },
        volume: volume / 100
      });
      
      player.addListener('ready', ({ device_id }) => {
        console.log('Spotify Player Ready with Device ID:', device_id);
        setPlayerDeviceId(device_id);
        setSdkReady(true);
        toast.success('Workout Music Player ready!');
      });
      
      player.addListener('not_ready', ({ device_id }) => {
        console.log('Device has gone offline:', device_id);
        setSdkReady(false);
      });
      
      player.addListener('player_state_changed', (state) => {
        if (!state) return;
        
        setIsPlaying(!state.paused);
        setProgress(state.position);
        
        if (state.track_window?.current_track) {
          const track = state.track_window.current_track;
          setCurrentTrack({
            id: track.id,
            name: track.name,
            artists: track.artists.map(a => a.name),
            album: track.album.name,
            album_image: track.album.images[0]?.url,
            duration_ms: track.duration_ms,
            uri: track.uri
          });
        }
      });
      
      player.connect();
      playerRef.current = player;
    };
    
    return () => {
      if (playerRef.current) {
        playerRef.current.disconnect();
      }
    };
  }, [spotifyToken, isPremium]);
  
  // Progress update interval
  useEffect(() => {
    let interval;
    if (isPlaying && currentTrack) {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= currentTrack.duration_ms) {
            return 0;
          }
          return prev + 1000;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTrack]);
  
  const handleOAuthCallback = async (code) => {
    setIsConnecting(true);
    try {
      const response = await axios.get(`${API}/spotify/callback?code=${code}`);
      const { access_token, refresh_token } = response.data;
      
      localStorage.setItem('spotify_access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('spotify_refresh_token', refresh_token);
      }
      
      setSpotifyToken(access_token);
      
      // Clean URL
      window.history.replaceState({}, document.title, '/workout-music');
      toast.success('Connected to Spotify!');
    } catch (error) {
      console.error('OAuth callback error:', error);
      toast.error('Failed to connect to Spotify');
    } finally {
      setIsConnecting(false);
    }
  };
  
  const connectSpotify = async () => {
    setIsConnecting(true);
    try {
      const response = await axios.get(`${API}/spotify/auth/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Spotify login error:', error);
      toast.error('Failed to connect to Spotify');
      setIsConnecting(false);
    }
  };
  
  const disconnectSpotify = () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    setSpotifyToken(null);
    setSpotifyUser(null);
    setIsPremium(false);
    setCurrentTrack(null);
    setDjPlaylist(null);
    toast.success('Disconnected from Spotify');
  };
  
  const loadSpotifyProfile = async () => {
    try {
      const response = await axios.get(`${API}/spotify/user/profile?access_token=${spotifyToken}`);
      setSpotifyUser(response.data);
      setIsPremium(response.data.is_premium);
      
      if (!response.data.is_premium) {
        toast.info('Spotify Premium is required for full playback control. Preview mode available.');
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      if (error.response?.status === 401) {
        const refreshToken = localStorage.getItem('spotify_refresh_token');
        if (refreshToken) {
          try {
            const refreshResponse = await axios.post(`${API}/spotify/auth/refresh?refresh_token=${refreshToken}`);
            const newToken = refreshResponse.data.access_token;
            localStorage.setItem('spotify_access_token', newToken);
            setSpotifyToken(newToken);
          } catch {
            disconnectSpotify();
          }
        } else {
          disconnectSpotify();
        }
      }
    }
  };
  
  const loadDevices = async () => {
    try {
      const response = await axios.get(`${API}/spotify/devices?access_token=${spotifyToken}`);
      setDevices(response.data.devices);
      
      const activeDevice = response.data.devices.find(d => d.is_active);
      if (activeDevice) {
        setSelectedDevice(activeDevice.id);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };
  
  const loadFeaturedPlaylists = async () => {
    try {
      const response = await axios.get(`${API}/spotify/playlists/featured-workout?access_token=${spotifyToken}`);
      setFeaturedPlaylists(response.data.playlists);
    } catch (error) {
      console.error('Failed to load playlists:', error);
    }
  };
  
  const searchTracks = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/spotify/search?q=${encodeURIComponent(searchQuery)}&access_token=${spotifyToken}`);
      setSearchResults(response.data.tracks);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };
  
  const searchWorkoutMusic = async () => {
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${API}/spotify/search/workout?genre=${selectedGenre}&energy=${selectedEnergy}&access_token=${spotifyToken}`
      );
      setSearchResults(response.data.tracks);
      setSearchQuery('');
    } catch (error) {
      console.error('Workout search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };
  
  // Workout DJ - Generate Playlist
  const generateDjPlaylist = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${API}/spotify/workout-dj/generate?access_token=${spotifyToken}`,
        {
          workout_type: djWorkoutType,
          duration_minutes: djDuration,
          include_warmup: djIncludeWarmup,
          include_cooldown: djIncludeCooldown
        }
      );
      
      setDjPlaylist(response.data);
      setCurrentPlaylistTracks(response.data.tracks);
      toast.success(`🎧 ${response.data.playlist_name} created!`);
    } catch (error) {
      console.error('DJ playlist error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate playlist');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const loadPlaylistTracks = async (playlist) => {
    setSelectedPlaylist(playlist);
    try {
      const response = await axios.get(
        `${API}/spotify/playlists/${playlist.id}/tracks?access_token=${spotifyToken}`
      );
      setPlaylistTracks(response.data.tracks);
    } catch (error) {
      console.error('Failed to load playlist tracks:', error);
      toast.error('Failed to load playlist');
    }
  };
  
  const playTrack = async (track, trackList = null, trackIndex = 0) => {
    // Stop any preview audio
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    
    if (!isPremium) {
      // For free users, play preview if available
      if (track.preview_url) {
        const audio = new Audio(track.preview_url);
        audio.volume = volume / 100;
        previewAudioRef.current = audio;
        audio.play();
        setCurrentTrack(track);
        setIsPlaying(true);
        setProgress(0);
        toast.info('Playing 30-second preview (Premium required for full track)');
        
        // Update progress for preview
        const progressInterval = setInterval(() => {
          if (audio.currentTime) {
            setProgress(audio.currentTime * 1000);
          }
        }, 1000);
        
        audio.onended = () => {
          clearInterval(progressInterval);
          setIsPlaying(false);
          setProgress(0);
          // Auto-play next track in playlist
          if (trackList && trackIndex < trackList.length - 1) {
            playTrack(trackList[trackIndex + 1], trackList, trackIndex + 1);
          }
        };
        return;
      } else {
        toast.error('No preview available. Spotify Premium required for full playback.');
        return;
      }
    }
    
    try {
      const deviceId = playerDeviceId || selectedDevice;
      await axios.post(
        `${API}/spotify/play?access_token=${spotifyToken}`,
        {
          track_uri: track.uri,
          position_ms: 0,
          device_id: deviceId
        }
      );
      
      setCurrentTrack(track);
      setIsPlaying(true);
      setProgress(0);
      if (trackList) {
        setCurrentPlaylistTracks(trackList);
        setCurrentTrackIndex(trackIndex);
      }
    } catch (error) {
      console.error('Play error:', error);
      toast.error(error.response?.data?.detail || 'Failed to play track');
    }
  };
  
  const playAllDjPlaylist = () => {
    if (djPlaylist && djPlaylist.tracks.length > 0) {
      playTrack(djPlaylist.tracks[0], djPlaylist.tracks, 0);
    }
  };
  
  const togglePlayPause = async () => {
    if (!isPremium && previewAudioRef.current) {
      if (isPlaying) {
        previewAudioRef.current.pause();
      } else {
        previewAudioRef.current.play();
      }
      setIsPlaying(!isPlaying);
      return;
    }
    
    if (!isPremium) return;
    
    try {
      if (isPlaying) {
        await axios.post(`${API}/spotify/pause?access_token=${spotifyToken}`);
      } else {
        await axios.post(`${API}/spotify/resume?access_token=${spotifyToken}`);
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error('Playback error:', error);
    }
  };
  
  const skipNext = async () => {
    if (currentPlaylistTracks.length > 0 && currentTrackIndex < currentPlaylistTracks.length - 1) {
      playTrack(currentPlaylistTracks[currentTrackIndex + 1], currentPlaylistTracks, currentTrackIndex + 1);
      return;
    }
    
    if (!isPremium) return;
    try {
      await axios.post(`${API}/spotify/next?access_token=${spotifyToken}`);
    } catch (error) {
      console.error('Skip error:', error);
    }
  };
  
  const skipPrevious = async () => {
    if (currentPlaylistTracks.length > 0 && currentTrackIndex > 0) {
      playTrack(currentPlaylistTracks[currentTrackIndex - 1], currentPlaylistTracks, currentTrackIndex - 1);
      return;
    }
    
    if (!isPremium) return;
    try {
      await axios.post(`${API}/spotify/previous?access_token=${spotifyToken}`);
    } catch (error) {
      console.error('Previous error:', error);
    }
  };
  
  const handleVolumeChange = async (newVolume) => {
    setVolume(newVolume);
    if (previewAudioRef.current) {
      previewAudioRef.current.volume = newVolume / 100;
    }
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume / 100);
    }
    if (isPremium) {
      try {
        await axios.post(`${API}/spotify/volume?volume_percent=${newVolume}&access_token=${spotifyToken}`);
      } catch (error) {
        console.error('Volume error:', error);
      }
    }
  };
  
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Get section color for DJ playlist
  const getSectionColor = (section) => {
    switch (section) {
      case 'warmup': return 'bg-yellow-500/20 border-yellow-500/50';
      case 'cooldown': return 'bg-blue-500/20 border-blue-500/50';
      default: return 'bg-primary/20 border-primary/50';
    }
  };
  
  const getSectionBadge = (section) => {
    switch (section) {
      case 'warmup': return <span className="px-2 py-0.5 bg-yellow-500/30 text-yellow-400 text-xs rounded-full">Warm-up</span>;
      case 'cooldown': return <span className="px-2 py-0.5 bg-blue-500/30 text-blue-400 text-xs rounded-full">Cool-down</span>;
      default: return null;
    }
  };
  
  // Not connected state
  if (!spotifyToken) {
    return (
      <div className="space-y-6" data-testid="workout-music-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Music className="w-6 h-6 text-primary" />
              Workout Music
            </h1>
            <p className="text-gray-400">Power your workouts with the perfect playlist</p>
          </div>
        </div>
        
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Music className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">Connect to Spotify</h2>
            <p className="text-gray-400 mb-6">
              Link your Spotify account to access millions of workout tracks and curated playlists.
            </p>
            
            <Button
              onClick={connectSpotify}
              disabled={isConnecting}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg"
              data-testid="connect-spotify-btn"
            >
              {isConnecting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <LogIn className="w-5 h-5 mr-2" />
              )}
              {isConnecting ? 'Connecting...' : 'Connect with Spotify'}
            </Button>
            
            <p className="text-xs text-gray-500 mt-4">
              Spotify Premium recommended for full playback control
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="workout-music-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Music className="w-6 h-6 text-primary" />
            Workout Music
          </h1>
          <p className="text-gray-400">Power your workouts with the perfect playlist</p>
        </div>
        
        {/* User Info */}
        <div className="flex items-center gap-3">
          {spotifyUser && (
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
              {spotifyUser.image_url && (
                <img src={spotifyUser.image_url} alt="" className="w-8 h-8 rounded-full" />
              )}
              <div className="text-sm">
                <div className="text-white font-medium">{spotifyUser.display_name}</div>
                <div className="flex items-center gap-1 text-xs">
                  {isPremium ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Premium
                    </span>
                  ) : (
                    <span className="text-gray-400">Free</span>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={disconnectSpotify}
            className="border-border"
          >
            Disconnect
          </Button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        <button
          onClick={() => setActiveTab('dj')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'dj' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="tab-workout-dj"
        >
          <Sparkles className="w-4 h-4" />
          Workout DJ
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'search' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="tab-search"
        >
          <Search className="w-4 h-4" />
          Search
        </button>
        <button
          onClick={() => setActiveTab('playlists')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
            activeTab === 'playlists' ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="tab-playlists"
        >
          <ListMusic className="w-4 h-4" />
          Playlists
        </button>
      </div>
      
      {/* Workout DJ Tab */}
      {activeTab === 'dj' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* DJ Controls */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-gradient-to-br from-purple-900/50 to-purple-600/20 border border-purple-500/30 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Workout DJ
              </h2>
              <p className="text-sm text-gray-400 mb-6">
                AI creates the perfect playlist for your workout type and duration
              </p>
              
              {/* Workout Type Selection */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Workout Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {WORKOUT_DJ_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setDjWorkoutType(type.id)}
                        className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                          djWorkoutType === type.id
                            ? `${type.bgColor} border-current ${type.color}`
                            : 'border-border text-gray-400 hover:border-gray-600'
                        }`}
                        data-testid={`dj-type-${type.id}`}
                      >
                        <Icon className={`w-5 h-5 ${djWorkoutType === type.id ? type.color : ''}`} />
                        <span className="text-xs font-medium">{type.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Duration Selection */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2 flex items-center gap-1">
                  <Timer className="w-4 h-4" />
                  Duration
                </label>
                <div className="flex flex-wrap gap-2">
                  {DURATION_OPTIONS.map(dur => (
                    <button
                      key={dur}
                      onClick={() => setDjDuration(dur)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        djDuration === dur
                          ? 'bg-purple-500 text-white'
                          : 'bg-secondary text-gray-300 hover:bg-secondary/80'
                      }`}
                    >
                      {dur} min
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Options */}
              <div className="mb-6 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={djIncludeWarmup}
                    onChange={(e) => setDjIncludeWarmup(e.target.checked)}
                    className="w-4 h-4 rounded bg-secondary border-border text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-300">Include warm-up tracks</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={djIncludeCooldown}
                    onChange={(e) => setDjIncludeCooldown(e.target.checked)}
                    className="w-4 h-4 rounded bg-secondary border-border text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-300">Include cool-down tracks</span>
                </label>
              </div>
              
              {/* Generate Button */}
              <Button
                onClick={generateDjPlaylist}
                disabled={isGenerating}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white py-6 text-lg"
                data-testid="generate-dj-playlist-btn"
              >
                {isGenerating ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-5 h-5 mr-2" />
                )}
                {isGenerating ? 'Creating Mix...' : 'Generate Playlist'}
              </Button>
            </div>
          </div>
          
          {/* Generated Playlist */}
          <div className="lg:col-span-2">
            {djPlaylist ? (
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{djPlaylist.playlist_name}</h3>
                    <p className="text-sm text-gray-400">
                      {djPlaylist.track_count} tracks • {djPlaylist.actual_duration_minutes} min • {djPlaylist.energy_level} energy
                    </p>
                  </div>
                  <Button
                    onClick={playAllDjPlaylist}
                    className="bg-green-500 hover:bg-green-600"
                    data-testid="play-all-dj-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Play All
                  </Button>
                </div>
                
                {/* Section breakdown */}
                {(djPlaylist.sections.warmup > 0 || djPlaylist.sections.cooldown > 0) && (
                  <div className="flex gap-2 mb-4">
                    {djPlaylist.sections.warmup > 0 && (
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                        {djPlaylist.sections.warmup} warm-up
                      </span>
                    )}
                    <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                      {djPlaylist.sections.main} main
                    </span>
                    {djPlaylist.sections.cooldown > 0 && (
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                        {djPlaylist.sections.cooldown} cool-down
                      </span>
                    )}
                  </div>
                )}
                
                {/* Track List */}
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {djPlaylist.tracks.map((track, index) => (
                    <div
                      key={`${track.id}-${index}`}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer border ${
                        currentTrack?.id === track.id
                          ? 'bg-green-500/20 border-green-500/50'
                          : getSectionColor(track.section)
                      }`}
                      onClick={() => playTrack(track, djPlaylist.tracks, index)}
                    >
                      <div className="text-gray-500 w-6 text-center text-sm">{index + 1}</div>
                      
                      {track.album_image ? (
                        <img src={track.album_image} alt={track.album} className="w-10 h-10 rounded" />
                      ) : (
                        <div className="w-10 h-10 bg-secondary rounded flex items-center justify-center">
                          <Music className="w-5 h-5 text-gray-500" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium truncate">{track.name}</span>
                          {getSectionBadge(track.section)}
                        </div>
                        <div className="text-sm text-gray-400 truncate">{track.artists.join(', ')}</div>
                      </div>
                      
                      <div className="text-sm text-gray-400">{formatTime(track.duration_ms)}</div>
                      
                      {currentTrack?.id === track.id && isPlaying && (
                        <div className="flex gap-0.5">
                          <span className="w-1 h-3 bg-green-500 animate-pulse"></span>
                          <span className="w-1 h-4 bg-green-500 animate-pulse delay-75"></span>
                          <span className="w-1 h-2 bg-green-500 animate-pulse delay-150"></span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-purple-400/30" />
                <h3 className="text-lg font-medium text-white mb-2">Your Workout Mix Awaits</h3>
                <p className="text-gray-400 mb-4">
                  Select your workout type and duration, then let Workout DJ create the perfect playlist for you.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* Quick Workout Search */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Find Workout Music
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Genre</label>
                <div className="flex flex-wrap gap-2">
                  {WORKOUT_GENRES.map(genre => (
                    <button
                      key={genre.id}
                      onClick={() => setSelectedGenre(genre.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedGenre === genre.id
                          ? 'bg-primary text-white'
                          : 'bg-secondary text-gray-300 hover:bg-secondary/80'
                      }`}
                    >
                      {genre.icon} {genre.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Energy Level</label>
                <div className="flex flex-wrap gap-2">
                  {ENERGY_LEVELS.map(level => (
                    <button
                      key={level.id}
                      onClick={() => setSelectedEnergy(level.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        selectedEnergy === level.id
                          ? 'bg-primary text-white'
                          : 'bg-secondary text-gray-300 hover:bg-secondary/80'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${level.color}`}></span>
                      {level.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <Button
              onClick={searchWorkoutMusic}
              disabled={isSearching}
              className="bg-primary hover:bg-red-700"
            >
              {isSearching ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Dumbbell className="w-4 h-4 mr-2" />
              )}
              Find Workout Tracks
            </Button>
          </div>
          
          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchTracks()}
                placeholder="Search for songs, artists, or albums..."
                className="w-full pl-10 pr-4 py-3 bg-secondary border border-border rounded-lg text-white"
                data-testid="search-input"
              />
            </div>
            <Button
              onClick={searchTracks}
              disabled={isSearching || !searchQuery.trim()}
              className="bg-green-500 hover:bg-green-600"
            >
              {isSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </Button>
          </div>
          
          {/* Search Results */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {searchResults.map((track, index) => (
              <div
                key={`${track.id}-${index}`}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                  currentTrack?.id === track.id
                    ? 'bg-primary/20 border border-primary/50'
                    : 'bg-card border border-border hover:border-gray-600'
                }`}
                onClick={() => playTrack(track, searchResults, index)}
              >
                {track.album_image ? (
                  <img src={track.album_image} alt={track.album} className="w-12 h-12 rounded" />
                ) : (
                  <div className="w-12 h-12 bg-secondary rounded flex items-center justify-center">
                    <Music className="w-6 h-6 text-gray-500" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{track.name}</div>
                  <div className="text-sm text-gray-400 truncate">{track.artists.join(', ')}</div>
                </div>
                
                <div className="text-sm text-gray-400">{formatTime(track.duration_ms)}</div>
                
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-10 h-10 rounded-full p-0 hover:bg-green-500/20"
                  onClick={(e) => { e.stopPropagation(); playTrack(track, searchResults, index); }}
                >
                  <Play className="w-5 h-5 text-green-500" />
                </Button>
              </div>
            ))}
            
            {searchResults.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Search className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Search for tracks or browse workout music</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Playlists Tab */}
      {activeTab === 'playlists' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Playlist List */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Heart className="w-4 h-4 text-primary" />
              Workout Playlists
            </h3>
            
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {featuredPlaylists.map((playlist) => (
                <div
                  key={playlist.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedPlaylist?.id === playlist.id
                      ? 'bg-green-500/20 border border-green-500/50'
                      : 'bg-card border border-border hover:border-gray-600'
                  }`}
                  onClick={() => loadPlaylistTracks(playlist)}
                >
                  {playlist.image_url ? (
                    <img src={playlist.image_url} alt={playlist.name} className="w-12 h-12 rounded object-cover" />
                  ) : (
                    <div className="w-12 h-12 bg-secondary rounded flex items-center justify-center">
                      <Music className="w-6 h-6 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate text-sm">{playlist.name}</div>
                    <div className="text-xs text-gray-400">{playlist.tracks_total} tracks</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                </div>
              ))}
              
              {featuredPlaylists.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">Loading playlists...</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Playlist Tracks */}
          <div className="lg:col-span-2">
            {selectedPlaylist && playlistTracks.length > 0 ? (
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selectedPlaylist.name}</h3>
                    <p className="text-sm text-gray-400">{playlistTracks.length} tracks</p>
                  </div>
                  <Button
                    onClick={() => { setSelectedPlaylist(null); setPlaylistTracks([]); }}
                    variant="ghost"
                    size="sm"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {playlistTracks.map((track, index) => (
                    <div
                      key={`${track.id}-${index}`}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                        currentTrack?.id === track.id
                          ? 'bg-green-500/20 border border-green-500/50'
                          : 'bg-secondary/50 hover:bg-secondary'
                      }`}
                      onClick={() => playTrack(track, playlistTracks, index)}
                    >
                      <div className="text-gray-500 w-6 text-center text-sm">{index + 1}</div>
                      
                      {track.album_image ? (
                        <img src={track.album_image} alt={track.album} className="w-10 h-10 rounded" />
                      ) : (
                        <div className="w-10 h-10 bg-secondary rounded flex items-center justify-center">
                          <Music className="w-5 h-5 text-gray-500" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">{track.name}</div>
                        <div className="text-sm text-gray-400 truncate">{track.artists.join(', ')}</div>
                      </div>
                      
                      <div className="text-sm text-gray-400">{formatTime(track.duration_ms)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <ListMusic className="w-16 h-16 mx-auto mb-4 text-gray-500/30" />
                <h3 className="text-lg font-medium text-white mb-2">Select a Playlist</h3>
                <p className="text-gray-400">Choose a workout playlist from the list to see its tracks</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Now Playing Bar */}
      {currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            {/* Track Info */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {currentTrack.album_image && (
                <img src={currentTrack.album_image} alt="" className="w-14 h-14 rounded shadow-lg" />
              )}
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{currentTrack.name}</div>
                <div className="text-sm text-gray-400 truncate">{currentTrack.artists.join(', ')}</div>
              </div>
            </div>
            
            {/* Playback Controls */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-4">
                <button
                  onClick={skipPrevious}
                  className="text-gray-400 hover:text-white"
                >
                  <SkipBack className="w-5 h-5" />
                </button>
                
                <button
                  onClick={togglePlayPause}
                  className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-black" />
                  ) : (
                    <Play className="w-6 h-6 text-black ml-1" />
                  )}
                </button>
                
                <button
                  onClick={skipNext}
                  className="text-gray-400 hover:text-white"
                >
                  <SkipForward className="w-5 h-5" />
                </button>
              </div>
              
              {/* Progress Bar */}
              <div className="flex items-center gap-2 w-64">
                <span className="text-xs text-gray-400 w-10 text-right">{formatTime(progress)}</span>
                <div className="flex-1 h-1 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${(progress / (currentTrack.duration_ms || 30000)) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-10">{formatTime(currentTrack.duration_ms || 30000)}</span>
              </div>
            </div>
            
            {/* Volume Control */}
            <div className="flex items-center gap-2 w-32">
              <button
                onClick={() => handleVolumeChange(volume === 0 ? 50 : 0)}
                className="text-gray-400 hover:text-white"
              >
                {volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                className="w-full accent-green-500"
              />
            </div>
            
            {/* SDK Status */}
            {isPremium && (
              <div className={`text-xs px-2 py-1 rounded ${sdkReady ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {sdkReady ? 'Player Ready' : 'Initializing...'}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Padding for fixed bottom bar */}
      {currentTrack && <div className="h-24"></div>}
    </div>
  );
}
